// src/components/phase05/dischargeAdvice/DischargeAdvicePrint.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDischargeAdvice } from "../../../store/slices/dischargeAdviceSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function DischargeAdvicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentDischargeAdvice, loading, error } = useSelector(
    (state) => state.dischargeAdvice
  );

  useEffect(() => {
    if (id) dispatch(fetchDischargeAdvice(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDischargeAdvice) {
      const timer = setTimeout(() => window.print(), 600);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentDischargeAdvice, navigate]);

  if (loading) return <div className="p-8 text-center">Loading…</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentDischargeAdvice) return <div className="p-8 text-center">No record found.</div>;

  const d = currentDischargeAdvice;

  const reasons = [
    "Rehab goals achieved to the desired level.",
    "Discharge against medical advice (Client’s family’s insistence)",
    "Resistant to Rehab interventions.",
    "Any medical condition/emergency.",
  ];

  const isChecked = (reason) => d.discharge_reason === reason;

  return (
    <>
      {/* UNIFIED HOSPITAL-GRADE PRINT STYLES */}
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
        }
        body {
          font-family: Arial, sans-serif !important;
          background: #fff !important;
          color: #000 !important;
          line-height: 1.6 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 20px !important;
          position: relative !important;
        }
        ${printFooterStyles}

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }
        .logo img {
          height: 60px !important;
          width: auto !important;
        }

        /* Header */
        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        
        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: semibold !important;
          color: #000000 !important;
        }

        .info-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 14px !important;
          font-size: 1rem !important;
        }

        .underline {
          display: inline-block !important;
          width: auto !important;
          border-bottom: 1px solid #000 !important;
          margin-left: 5px !important;
        }

        table.med-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 15px !important;
        }

        table.med-table th,
        table.med-table td {
          border: 1px solid #000 !important;
          padding: 8px !important;
          text-align: left !important;
          vertical-align: top !important;
        }

        table.med-table th {
          background-color: #f8f9fa !important;
          font-weight: bold !important;
          text-align: center !important;
        }

        .section-title {
          font-weight: bold !important;
          font-size: 1rem !important;
          margin: 24px 0 12px !important;
        }

        .checkbox {
          width: 16px !important;
          height: 16px !important;
          border: 1.8px solid #000 !important;
          display: inline-block !important;
          margin-right: 10px !important;
          position: relative !important;
        }
        .checkbox.checked::after {
          content: "✓" !important;
          position: absolute !important;
          top: -6px !important;
          left: 1px !important;
          font-weight: bold !important;
          font-size: 14px !important;
          color: #000 !important;
        }

        .reason-list {
          margin: 12px 0 20px 30px !important;
          line-height: 2 !important;
        }

        @media print {
          .print-container { padding: 15px !important; }
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="logo"><img src={logo} alt="The Recovery House" /></div>
          <div className="header">
            <h3>THE RECOVERY HOUSE</h3>
            <h6>DISCHARGE ADVICE FORM</h6>
          </div>

        {/* Client Info */}
        <div style={{ marginTop: "15px" }}>
          <div className="client-row">
            <div>
              <span>Client's Name:</span>
              <span className="underline fw-bold">{d.client_name || ""}</span>
            </div>
            <div>
              <span>Assigned PRP:</span>
              <span className="underline fw-bold">{d.assigned_prp || ""}</span>
            </div>
          </div>

          <div className="client-row">
            <div>
              <span>Psychiatric Diagnosis:</span>
              <span className="underline fw-bold">{d.psychiatric_diagnosis || ""}</span>
            </div>
            <div>
              <span>Primary Caretaker:</span>
              <span className="underline fw-bold">{d.primary_caretaker || ""}</span>
            </div>
          </div>

          <div className="client-row">
            <div>
              <span>Date of Admission:</span>
              <span className="underline fw-bold">{formatDate(d.admission_date) || ""}</span>
            </div>
            <div>
              <span>Date of Discharge:</span>
              <span className="underline fw-bold">{formatDate(d.discharge_date) || ""}</span>
            </div>
          </div>
        </div>

        {/* Reasons for Discharge */}
        <div className="section-title">Reasons for Discharge:</div>
        <div className="reason-list" style={{ textAlign: "center", margin: "20px 0" }}>
          {reasons.map((reason, i) => (
            <div
              key={i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                margin: "8px 0",
                fontSize: "1rem",
              }}
            >
              <span className={`checkbox ${isChecked(reason) ? "checked" : ""}`} />
              <span style={{ marginLeft: "10px" }}>{reason}</span>
            </div>
          ))}

          {/* Other Reason */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              margin: "12px 0 8px",
              fontSize: "1rem",
            }}
          >
            <span className={`checkbox ${d.other_reason ? "checked" : ""}`} />
            <span style={{ marginLeft: "10px", marginRight: "10px" }}>Other:</span>
            <span className="underline fw-bold" style={{ display: "inline-block" }}>
              {d.other_reason || ""}
            </span>
          </div>
        </div>

        {/* Discharge Authority */}
        <div className="section-title">Discharge Authority/Committee Signature:</div>
        <div style={{ marginLeft: "40px", lineHeight: "2.2" }}>
          <div>1. PRP: <span className="underline">{d.assigned_prp || ""}</span></div>
          <div>2. Medical Doctor: <span className="underline">{d.approved_by || ""}</span></div>
        </div>

        {/* Endorsed By */}
        <div className="section-title">Endorsed By:</div>
        <div style={{ marginLeft: "40px", lineHeight: "2.2" }}>
          <div>1. Accounts Head: <span className="underline">{d.next_revision_by || ""}</span></div>
          <div>2. Director: <span className="underline">{d.creator?.name || ""}</span></div>
        </div>

        {/* Remarks */}
        <div className="section-title">Discharge Remarks, if any:</div>
        <div style={{ margin: "12px 0" }}>
          By Director: <span className="underline" style={{ display: "inline-block" }}>
            {d.discharge_reason || ""}
          </span>
        </div>

        {/* Footer */}
        <div className="footer">

          <div className="footer-meta">
            <div><strong>Name of Document:</strong> <span className="underline"> Progress Notes (Physician)</span></div>
            <div><strong>Effective Date:</strong> <span className="underline">{formatDate(d.effective_date) || ""}</span></div>
          </div>

          <div className="footer-sign">
            <div><strong>Approved by:</strong> <span className="underline">{d.approved_by || ""}</span></div>
            <div><strong>Approved Date:</strong> <span className="underline">{formatDate(d.approved_date) || ""}</span></div>
          </div>
          <div className="footer-sign">
            <div><strong>Next Revision by:</strong> <span className="underline">{d.next_revision_by || ""}</span></div>
            <div><strong>Next Revision Date:</strong> <span className="underline">{formatDate(d.next_revision_date) || ""}</span></div>
          </div>
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}